/**
 * JoinLine — renders one JOIN strip beneath a FROM item: left-anchor (the
 * previous FROM item), arrow, kind dropdown, RHS table / subquery, ON pills
 * (split by AND/OR) or a USING pill, plus a trailing remove `×`. When an
 * `editContext` with a ready schema snapshot is supplied, the kind dropdown,
 * ON-pill editing and remove are live; without one, each control renders its
 * § 1.4 disabled scaffold with the standard `vqeCopy.disabled` tooltip.
 *
 * Subquery wiring: when the JOIN's RHS is a derived table or a `LATERAL`
 * group, the RHS span is replaced with a `<SubquerySection mode='block'>` so
 * it can be expanded / focused / drilled into. Plain reference RHS (table /
 * function call) still renders the original mono span.
 *
 * COM-21/COM-22: the expanded RHS derived-table body is now inline-editable
 * (threads this line's `editContext`, gated on `editReady`) rather than a
 * drill-only preview — including LATERAL, since `resolveScope` already scopes
 * a LATERAL frame's correlated outer aliases correctly.
 *
 * SEL-22: an optional `compact` prop selects a DENSE single-line layout for
 * the compact popover (root goes `flex-nowrap`, tighter gap/padding, a
 * `data-density="compact"` marker). It's a pure density change — every
 * control (kind dropdown, ON/USING affordance, remove) stays reachable and,
 * when `editContext` is live, editable; nothing is dropped. The one shape
 * change: a multi-condition ON collapses from `OnPills`' per-fragment list
 * (which has its own internal `flex-wrap` and would defeat single-line) into
 * ONE summarized, truncatable pill showing the full predicate, with the full
 * text also available via its tooltip. `compact` falsy renders byte-for-byte
 * identical to before this change.
 */

import { ChevronDown } from 'lucide-react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import { cn } from '../../../../../lib/utils';
import { badgeVariants } from '../../../../../components/ui/badge';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { removeJoin } from '../../edit/edits/removeJoin';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';
import type { JoinClause } from './extractFromJoins';
import { extractSubqueries } from './extractSubqueries';
import { JoinEditableOnPill } from './JoinEditableOnPill';
import { JoinKindDropdown } from './JoinKindDropdown';
import { KIND_ARIA, KIND_BADGE_CLASS, KIND_LABELS, KIND_TOOLTIPS } from './joinKindMaps';
import { OnPills, RhsSpan, UsingPill } from './JoinPredicates';
import type { SubqueryWiring } from './SelectRenderer';
import { SubquerySection } from './SubquerySection';
import { buildDrillLabel, extractRelationIdentifier } from '../../ux/drillLabel';
import { vqeCopy } from '../../ux/vqeCopy';

export interface JoinLineProps {
  readonly bufferText: string;
  readonly join: JoinClause;
  readonly options: ParseOptions;
  readonly leadingText: string;
  readonly testIdPrefix: string;
  readonly subqueryCtx?: SubqueryWiring;
  readonly drillLabel?: string;
  /** When supplied AND the resolver service has a snapshot, enables remove + ON-edit. */
  readonly editContext?: VqeEditContext;
  /** 0-based position of this JOIN in the full structure's `joins` list. */
  readonly joinIndex?: number;
  /**
   * SEL-22: renders the dense single-line compact-popover layout instead of
   * the full-mode wrapping row. Falsy (the default) renders exactly as
   * before — full mode is untouched.
   */
  readonly compact?: boolean;
  /**
   * Show the `<leading item> →` source prefix. `FromSection` hides it on a
   * segment's first join — the only table it can attach to is the row above.
   * Defaults to shown.
   */
  readonly showLeading?: boolean;
}

const TOOLTIP = vqeCopy.disabled.editingUnavailable;

export function JoinLine({
  bufferText,
  join,
  options,
  leadingText,
  testIdPrefix,
  subqueryCtx,
  drillLabel,
  editContext,
  joinIndex,
  compact,
  showLeading = true,
}: JoinLineProps) {
  const kindLabel = KIND_LABELS[join.kind];
  const derived = subqueryCtx
    ? extractSubqueries(bufferText, join.rhs.start, join.rhs.end, options)[0]
    : undefined;
  // Prefer an identifier-based label (JOIN <rhs relation/alias>). When the RHS
  // is an un-aliased derived table there's no identifier, so fall back to the
  // positional label the parent supplied (`drillLabel`), then to the kind.
  const rhsIdentifier = extractRelationIdentifier(join.rhs.text);
  const resolvedDrillLabel = rhsIdentifier
    ? buildDrillLabel({ kind: 'join', identifier: rhsIdentifier, positionalIndex: 0 })
    : drillLabel ?? `JOIN ${kindLabel}`;
  const editReady =
    !!editContext && !!editContext.service.getSchemaSnapshot() && typeof joinIndex === 'number';
  const onRemove =
    editReady && editContext && typeof joinIndex === 'number'
      ? () => {
          void removeJoin(joinIndex, toCommitCtx(editContext));
        }
      : undefined;

  // SEL-22: density-only class swaps. The `compact` branch always mirrors the
  // full-mode branch's SEMANTICS (same controls, same edit gating) — only
  // spacing/wrap/truncation differs, and every ternary's falsy arm is the
  // ORIGINAL literal string/JSX so full mode stays byte-for-byte unchanged.
  const rowClass = compact
    ? 'flex flex-nowrap items-center gap-1.5 rounded-md border border-dashed border-border bg-background/40 px-1.5 py-1'
    : 'flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-background/40 px-2 py-1.5';
  const lhsClass = compact
    ? 'min-w-0 shrink truncate font-mono text-[11px] text-muted-foreground'
    : 'font-mono text-[11px] text-muted-foreground';
  const arrowClass = compact ? 'shrink-0 text-muted-foreground/70' : 'text-muted-foreground/70';

  return (
    <div
      className={rowClass}
      data-testid={testIdPrefix}
      data-join-kind={join.kind}
      data-join-lateral={join.isLateral ? 'true' : 'false'}
      data-density={compact ? 'compact' : undefined}
    >
      {showLeading ? (
        <>
          <span className={lhsClass} data-testid={`${testIdPrefix}-lhs`}>
            {leadingText || '?'}
          </span>
          <span aria-hidden className={arrowClass}>→</span>
        </>
      ) : null}
      {editReady && editContext && typeof joinIndex === 'number' ? (
        compact ? (
          <span className="shrink-0">
            <JoinKindDropdown
              currentKind={join.kind}
              isLateral={join.isLateral}
              joinIndex={joinIndex}
              editContext={editContext}
              options={options}
              testIdPrefix={testIdPrefix}
            />
          </span>
        ) : (
          <JoinKindDropdown
            currentKind={join.kind}
            isLateral={join.isLateral}
            joinIndex={joinIndex}
            editContext={editContext}
            options={options}
            testIdPrefix={testIdPrefix}
          />
        )
      ) : (
        <VqeTooltip content={`${KIND_TOOLTIPS[join.kind]} · ${TOOLTIP}`}>
          <button
            type="button"
            disabled
            aria-disabled
            aria-label={`${KIND_ARIA[join.kind]} kind dropdown`}
            data-testid={`${testIdPrefix}-kind-dropdown`}
            className={cn(
              badgeVariants({ variant: 'secondary' }),
              compact ? 'shrink-0 gap-1 disabled:opacity-100 cursor-default' : 'gap-1 disabled:opacity-100 cursor-default',
              KIND_BADGE_CLASS[join.kind],
            )}
          >
            <span data-testid={`${testIdPrefix}-kind-label`}>{kindLabel}</span>
            {join.isLateral ? (
              <span
                className="ml-1 rounded bg-teal-500 px-1 py-0 text-[10px] font-semibold uppercase tracking-wide text-white"
                data-testid={`${testIdPrefix}-lateral`}
              >
                LATERAL
              </span>
            ) : null}
            <ChevronDown className="h-3 w-3" aria-hidden />
          </button>
        </VqeTooltip>
      )}
      {derived && subqueryCtx ? (
        <SubquerySection
          bufferText={bufferText}
          bodyStart={derived.bodyStart}
          bodyEnd={derived.bodyEnd}
          bodyKind={derived.bodyKind}
          options={options}
          parentStatementId={subqueryCtx.statementId}
          drillTabId={subqueryCtx.drillTabId}
          drillLabel={resolvedDrillLabel}
          outerAliases={subqueryCtx.outerAliases}
          mode="block"
          testIdPrefix={`${testIdPrefix}-rhs-sub`}
          isLateral={join.isLateral}
          onEditAsSql={subqueryCtx.onEditAsSql}
          // COM-21/COM-22: thread editContext into the JOIN RHS derived-table
          // body, gated on the SAME `editReady` this line already computes for
          // its kind-dropdown/ON-pill/remove affordances. LATERAL is included
          // (not gated out) — `resolveScope` already restricts a LATERAL
          // frame's parent scope to pre-frame aliases only (lateralFrame.ts),
          // so correlation stays correct without extra gating here.
          editContext={editReady && editContext ? editContext : undefined}
        />
      ) : compact ? (
        <span className="min-w-0 shrink truncate">
          <RhsSpan text={join.rhs.text} testIdPrefix={testIdPrefix} />
        </span>
      ) : (
        <RhsSpan text={join.rhs.text} testIdPrefix={testIdPrefix} />
      )}
      {join.on ? (
        compact ? (
          editReady && editContext && typeof joinIndex === 'number' ? (
            // `flex-auto` (basis = content), NOT `flex-1` (basis 0): a
            // basis-0 item gets NO share of NEGATIVE free space, so on a
            // crowded row this group stayed 0px wide while its shrink-0 ON
            // badge spilled out and overdrew the remove ×. `overflow-hidden`
            // is the hard guarantee that nothing escapes the row either way.
            <span className="flex min-w-0 flex-auto items-center gap-1 overflow-hidden">
              <span
                className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                data-testid={`${testIdPrefix}-on-label`}
              >
                ON
              </span>
              <span className="flex min-w-0 flex-1 items-center overflow-hidden">
                <JoinEditableOnPill
                  testId={`${testIdPrefix}-on-edit`}
                  text={join.on.text}
                  joinIndex={joinIndex}
                  editContext={editContext}
                />
              </span>
            </span>
          ) : (
            // A multi-condition ON renders as N separate AND-joined pills in
            // `OnPills` (its own internal `flex-wrap`), which would defeat
            // single-line regardless of this row's own `flex-nowrap`. Compact
            // collapses it to ONE summarized, truncatable pill — the full
            // predicate stays reachable via the tooltip on overflow.
            <span className="min-w-0 flex-auto truncate" data-testid={`${testIdPrefix}-on-summary`}>
              <VqeTooltip content={`${join.on.text} · ${TOOLTIP}`} block>
                <span className="block truncate rounded-full border border-border bg-muted/40 px-2 py-0.5 font-mono text-[11px]">
                  ON {join.on.text}
                </span>
              </VqeTooltip>
            </span>
          )
        ) : editReady && editContext && typeof joinIndex === 'number' ? (
          <>
            <span
              className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
              data-testid={`${testIdPrefix}-on-label`}
            >
              ON
            </span>
            <JoinEditableOnPill
              testId={`${testIdPrefix}-on-edit`}
              text={join.on.text}
              joinIndex={joinIndex}
              editContext={editContext}
            />
          </>
        ) : (
          <OnPills bufferText={bufferText} on={join.on} options={options} testIdPrefix={testIdPrefix} />
        )
      ) : null}
      {join.using ? (
        compact ? (
          <span className="flex min-w-0 flex-auto items-center overflow-hidden">
            <UsingPill using={join.using} testIdPrefix={testIdPrefix} />
          </span>
        ) : (
          <UsingPill using={join.using} testIdPrefix={testIdPrefix} />
        )
      ) : null}
      <span className={compact ? 'ml-auto shrink-0' : 'ml-auto'}>
        <RemoveButton
          testId={`${testIdPrefix}-remove`}
          ariaLabel={`Remove ${kindLabel.toLowerCase()}`}
          disabled={!editReady}
          tooltip={editReady ? `Remove ${kindLabel.toLowerCase()}` : undefined}
          onClick={onRemove}
          className="hover:bg-destructive/10 hover:text-destructive rounded"
        />
      </span>
    </div>
  );
}
